import React, { useCallback, useContext, useState } from 'react';
import ShareableLinkFileDownloadContext from './ShareableLinkFileDownload.context';
import DownloadModal from 'router/subrouters/DataRoom/components/DataRoomDocumentsRouter/components/DownloadModal';
import useCallbackRef from 'hooks/useCallbackRef';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import utils, {
  getDownloadName,
  getIsNodeFolder,
  getShareableChildIds,
  sortDataRoomNodes,
} from 'utils';
import shareableLinksDocuments from '../../api/shareable-links-documents';
import downloadService from 'services/downloadService';
import { SHAREABLE_LINK_LISTEN_EVENTS } from '../../services/socketService/socketEventNames';
import {
  DownloadedSuccessfullyEventData,
  DownloadFailedEventData,
} from '../../services/socketService/socketEventData';
import useSocketEvent from '../../services/socketService/hooks/useSocketEvent';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { prepareNodesForDownloadFileSelector } from 'providers/FileDownload/FileDownload.provider';
import { DOWNLOAD_BANNER_STATUS } from 'models/Download';
import DownloadStatusBanner from 'components/DownloadStatusBanner';
import useDownloadBanner from 'hooks/useDownloadBanner';

type ShareableLinkFileDownloadProps = Record<string, any>;

export const getSelectedNodes = (
  selectedNodes: DataRoomNode[],
  allNodes: DataRoomNode[],
): string[] =>
  (selectedNodes?.length ? selectedNodes : allNodes).flatMap((node) =>
    getIsNodeFolder(node) ? getShareableChildIds(node) : [node._id],
  );

const ShareableLinkFileDownload: React.FC<ShareableLinkFileDownloadProps> = (
  props,
) => {
  const { children } = props;

  const [isLoading, setIsLoading] = useState(false);

  const [initiallyCheckedNodes, setInitiallyCheckedNodes] = useState<string[]>(
    [],
  );

  const [nodes, setNodes] = useState([]);
  const [shareableLinkId, setShareableLinkId] = useState<string>();
  const [modal, modalRef] = useCallbackRef<ModalRef>(null);

  const { popServerError } = useContext(PopNotificationsContext);

  const { downloadStatus, downloadBannerRef, showDownloadBanner } =
    useDownloadBanner();

  const prepareInitialModalNodes = useCallback(
    (selectedFileIds: string[], nodes: DataRoomNode[]) => {
      const sortedNodes = sortDataRoomNodes(nodes);
      const pNodes = prepareNodesForDownloadFileSelector(
        sortedNodes,
        selectedFileIds,
      );

      setInitiallyCheckedNodes(selectedFileIds);
      setNodes(pNodes);
    },
    [],
  );

  const openDownloadModal = useCallback(
    async (
      shareableLinkId: string,
      selectedNodes: DataRoomNode[] = [],
      baseLevelNodes: DataRoomNode[] = [],
    ) => {
      setShareableLinkId(shareableLinkId);

      modal?.open();

      if (baseLevelNodes?.length) {
        prepareInitialModalNodes(
          getSelectedNodes(selectedNodes, baseLevelNodes),
          baseLevelNodes,
        );

        return;
      }

      try {
        setIsLoading(true);

        const { data } =
          await shareableLinksDocuments.getShareableLinkDocuments(
            shareableLinkId,
          );

        prepareInitialModalNodes(getSelectedNodes(selectedNodes, data), data);
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoading(false);
      }
    },
    [modal, popServerError, prepareInitialModalNodes],
  );

  const onCloseDownloadModal = useCallback(() => {
    setShareableLinkId(undefined);
    setNodes([]);
  }, []);

  const downloadBulk = useCallback(
    async (dataRoomNodes: DataRoomNode[]) => {
      const name = getDownloadName();

      try {
        showDownloadBanner(DOWNLOAD_BANNER_STATUS.PREPARING);
        await shareableLinksDocuments.bulkDownload(
          shareableLinkId,
          dataRoomNodes.map(({ _id }) => _id),
          name,
          downloadService.secureLinkDownloadUniqueId,
        );
      } catch {
        showDownloadBanner(DOWNLOAD_BANNER_STATUS.ERROR);
      } finally {
        modal?.close();
        onCloseDownloadModal();
      }
    },
    [modal, shareableLinkId, onCloseDownloadModal, showDownloadBanner],
  );

  const downloadSuccessfully = useCallback(
    ({ url, type, uniqueId }: DownloadedSuccessfullyEventData) => {
      if (uniqueId !== downloadService.secureLinkDownloadUniqueId) return;

      if (url) {
        utils.downloadUrl({ type, href: url });
        showDownloadBanner(DOWNLOAD_BANNER_STATUS.SUCCESS);
      } else {
        showDownloadBanner(DOWNLOAD_BANNER_STATUS.ERROR);
      }
    },
    [showDownloadBanner],
  );

  const downloadFailed = useCallback(
    ({ uniqueId }: DownloadFailedEventData) => {
      if (uniqueId !== downloadService.secureLinkDownloadUniqueId) return;

      showDownloadBanner(DOWNLOAD_BANNER_STATUS.ERROR);
    },
    [showDownloadBanner],
  );

  useSocketEvent(
    SHAREABLE_LINK_LISTEN_EVENTS.DOWNLOAD_SUCCESSFULLY,
    downloadSuccessfully,
  );
  useSocketEvent(SHAREABLE_LINK_LISTEN_EVENTS.DOWNLOAD_FAILED, downloadFailed);

  return (
    <ShareableLinkFileDownloadContext.Provider
      value={{ showDownloadBanner, openDownloadModal }}
    >
      {children}

      <DownloadStatusBanner
        ref={downloadBannerRef}
        variant={downloadStatus}
        onClose={() => showDownloadBanner(null)}
      />

      <DownloadModal
        nodes={nodes}
        isLoading={isLoading}
        initiallyCheckedNodes={initiallyCheckedNodes}
        ref={modalRef}
        onDownload={downloadBulk}
        onClose={onCloseDownloadModal}
        getChildren={(id: string) =>
          shareableLinksDocuments.getShareableLinkDocuments(shareableLinkId, id)
        }
      />
    </ShareableLinkFileDownloadContext.Provider>
  );
};

export default ShareableLinkFileDownload;
